import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

/**
 * The compact "origin ── truck ── destination" strip used in a task's list
 * row and again at the top of its detail view, so the same booking reads the
 * same way in both places.
 *
 * `progress` (0–1) fills the connecting line up to the truck icon — how far
 * through the pickup→drop leg the job is. Omit it for a flat line (job not
 * yet started, or the concept doesn't apply).
 */
export const RouteSummary: React.FC<{
  originLabel: string;
  originDate?: string;
  destinationLabel: string;
  destinationDate?: string;
  progress?: number;
  compact?: boolean;
}> = ({ originLabel, originDate, destinationLabel, destinationDate, progress, compact }) => {
  const pct = progress === undefined ? null : Math.max(0, Math.min(1, progress)) * 100;

  return (
    <Stack direction="row" alignItems="center" spacing={compact ? 1 : 1.5} sx={{ width: '100%' }}>
      <Box sx={{ minWidth: 0, flexShrink: 0, maxWidth: '38%' }}>
        <Typography
          variant={compact ? 'caption' : 'body2'}
          fontWeight={600}
          noWrap
          title={originLabel}
        >
          {originLabel}
        </Typography>
        {originDate && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {originDate}
          </Typography>
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 32, position: 'relative', display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            height: 2,
            width: '100%',
            bgcolor: 'divider',
            borderRadius: 1,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {pct !== null && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                width: `${pct}%`,
                bgcolor: 'primary.main',
                borderRadius: 1,
                transition: 'width 300ms ease',
              }}
            />
          )}
        </Box>
        <Box
          sx={{
            position: 'absolute',
            left: pct === null ? '50%' : `${pct}%`,
            transform: 'translateX(-50%)',
            display: 'grid',
            placeItems: 'center',
            width: compact ? 22 : 28,
            height: compact ? 22 : 28,
            borderRadius: '50%',
            bgcolor: 'background.paper',
            border: 2,
            borderColor: 'primary.main',
            color: 'primary.main',
            transition: 'left 300ms ease',
          }}
        >
          <LocalShippingIcon sx={{ fontSize: compact ? 13 : 16 }} />
        </Box>
      </Box>

      <Box sx={{ minWidth: 0, flexShrink: 0, maxWidth: '38%', textAlign: 'right' }}>
        <Typography
          variant={compact ? 'caption' : 'body2'}
          fontWeight={600}
          noWrap
          title={destinationLabel}
        >
          {destinationLabel}
        </Typography>
        {destinationDate && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {destinationDate}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};
